import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // For dynamic SEO tags
import TopBar from './components/TopBar';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import VideoSection from './components/VideoSection';
import FeatureBoxes from './components/FeatureBoxes';
import InfoSection from './components/InfoSection';
import ImageSlider from './components/ImageSlider';
import NewsletterSection from './components/NewsletterSection';
import CategoryGrid from './components/CategoryGrid';
import ContactForm from './ContactForm';
import RecruitingForm from './RecruitingForm';
import MadeInGermanyExcellenceSection from './components/MadeInGermanyExcellenceSection';
import AdviserSection from './components/AdviserSection';
import Footer from './components/Footer';
import CraftedExcellence from './CraftedExcellence';
import ComingSoon from './ComingSoon';
import WorldwideCraft from './WorldwideCraft';
import MadeInGermanyHistory from './MadeInGermanyHistory';
import LegalAndProtection from './LegalAndProtection';
import StrategicInvestments from './StrategicInvestments';
import PressContact from './PressContact';
import GermanBrandsSlider from './components/GermanBrandsSlider';
import Careers from './careers';
import GoUpButton from './components/GoUpButton';

// PreloaderText Component
const PreloaderText = ({ year }: { year: string }) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-8">
      <div className="space-y-4">
        <div className="text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-wider animate-pulse">
          MADE IN
        </div>
        <div className="text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-black via-red-600 to-yellow-400 bg-clip-text text-transparent animate-gradient">
          GERMANY
        </div>
      </div>
      <LoadingBar />
      <div className="text-sm md:text-base text-gray-400 tracking-widest animate-fade-in mt-4">
        © {year} PREMIUM QUALITY
      </div>
    </div>
  );
};

// LoadingBar Component
const LoadingBar = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => (prev < 100 ? prev + 1 : prev));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full max-w-md">
      <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-black via-red-600 to-yellow-400 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="text-gray-400 text-sm mt-2 text-center">{progress}%</div>
    </div>
  );
};

interface HeaderProps {
  onLanguageChange: (languageCode: string) => void;
}

function App() {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [loading, setLoading] = useState(true);
  const [year] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleLanguageChange = (languageCode: string) => {
    setSelectedLanguage(languageCode);
  };

  // Detect current domain
  const currentDomain = window.location.hostname;

  // Domain-specific SEO configurations
  const domainConfig = {
    'made-in-germany.uk': {
      title: 'Made in Germany UK | Premium German Quality Products',
      description: 'Buy premium German-made products in bulk for the UK. High-quality goods shipped fast.',
      canonical: 'https://made-in-germany.uk/',
      lang: 'en-gb',
    },
    'made-in-germany.world': {
      title: 'Made in Germany World | Premium German Products Globally',
      description: 'High-quality German goods in bulk, shipped worldwide.',
      canonical: 'https://made-in-germany.world/',
      lang: 'en',
    },
    'made-in-germany.global': {
      title: 'Made in Germany Global | Premium Quality Products',
      description: 'Explore premium German manufacturing for bulk orders worldwide.',
      canonical: 'https://made-in-germany.global/',
      lang: 'en',
    },
    'made-in-germany-first.com': {
      title: 'Made in Germany First | Top German Quality Products',
      description: 'Best German-made products in bulk for global markets.',
      canonical: 'https://made-in-germany-first.com/',
      lang: 'en',
    },
    'made-in-germany-arab.com': {
      title: 'Made in Germany Arab | منتجات ألمانية فاخرة',
      description: 'منتجات ألمانية عالية الجودة بكميات كبيرة للمنطقة العربية.',
      canonical: 'https://made-in-germany-arab.com/',
      lang: 'ar',
    },
    'made-in-germany-arabia.com': {
      title: 'Made in Germany Arabia | منتجات ألمانية مميزة',
      description: 'اكتشف المنتجات الألمانية الفاخرة بكميات كبيرة في المنطقة العربية.',
      canonical: 'https://made-in-germany-arabia.com/',
      lang: 'ar',
    },
    'made-in-germany.africa': {
      title: 'Made in Germany Africa | Premium German Goods',
      description: 'Bulk German quality products for Africa.',
      canonical: 'https://made-in-germany.africa/',
      lang: 'en',
    },
    'made-in-germany.asia': {
      title: 'Made in Germany Asia | Premium German Quality Products',
      description: 'High-quality German goods in bulk for Asia.',
      canonical: 'https://made-in-germany.asia/',
      lang: 'en',
    },
    'made-in-germany-china.com': {
      title: 'Made in Germany China | 德国优质产品',
      description: '德国制造的高品质产品，面向中国市场的大宗订单。',
      canonical: 'https://made-in-germany-china.com/',
      lang: 'zh',
    },
    'made-in-germany.my': {
      title: 'Made in Germany Malaysia | Premium German Products',
      description: 'Bulk German quality goods for Malaysia.',
      canonical: 'https://made-in-germany.my/',
      lang: 'ms',
    },
    'made-in-germany.com.in': {
      title: 'Made in Germany India | Premium German Quality Products',
      description: 'Buy premium German-made products in bulk for India.',
      canonical: 'https://made-in-germany.com.in/',
      lang: 'en-in',
    },
    'made-in-germany.co.in': {
      title: 'Made in Germany India | High-Quality German Goods',
      description: 'German craftsmanship in bulk for the Indian market.',
      canonical: 'https://made-in-germany.co.in/',
      lang: 'en-in',
    },
  };

  const config = domainConfig[currentDomain] || domainConfig['made-in-germany.global'];
  const keywords = 'Made in Germany products, German quality goods, bulk German products, premium German manufacturing, German exports worldwide, buy German products online';

  return (
    <Router>
      <>
        {/* Base Helmet for all pages */}
        <Helmet>
          <title>{config.title}</title>
          <meta name="description" content={config.description} />
          <meta name="keywords" content={keywords} />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={config.canonical} />
          {/* hrefLang for all domains */}
          <link rel="alternate" hrefLang="en-gb" href="https://made-in-germany.uk/" />
          <link rel="alternate" hrefLang="en" href="https://made-in-germany.world/" />
          <link rel="alternate" hrefLang="en" href="https://made-in-germany.global/" />
          <link rel="alternate" hrefLang="en" href="https://made-in-germany-first.com/" />
          <link rel="alternate" hrefLang="ar" href="https://made-in-germany-arab.com/" />
          <link rel="alternate" hrefLang="ar" href="https://made-in-germany-arabia.com/" />
          <link rel="alternate" hrefLang="en" href="https://made-in-germany.africa/" />
          <link rel="alternate" hrefLang="en" href="https://made-in-germany.asia/" />
          <link rel="alternate" hrefLang="zh" href="https://made-in-germany-china.com/" />
          <link rel="alternate" hrefLang="ms" href="https://made-in-germany.my/" />
          <link rel="alternate" hrefLang="en-in" href="https://made-in-germany.com.in/" />
          <link rel="alternate" hrefLang="en-in" href="https://made-in-germany.co.in/" />
          <link rel="alternate" hrefLang="x-default" href="https://made-in-germany.global/" />
          {/* Open Graph */}
          <meta property="og:title" content={config.title} />
          <meta property="og:description" content={config.description} />
          <meta property="og:url" content={config.canonical} />
          <meta property="og:image" content="https://made-in-germany.uk/og-image.webp" />
          <meta property="og:type" content="website" />
          {/* Twitter Cards */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={config.title} />
          <meta name="twitter:description" content={config.description} />
          <meta name="twitter:image" content="https://made-in-germany.uk/og-image.webp" />
        </Helmet>

        {loading ? (
          <div className="fixed inset-0 bg-[#0B111F] flex items-center justify-center">
            <div className="relative w-full max-w-4xl mx-4">
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-red-600/20 to-yellow-400/20 blur-3xl transform -skew-y-6 animate-pulse" />
              <div className="relative bg-[#0B111F]/90 p-12 rounded-lg backdrop-blur-sm border border-gray-800">
                <PreloaderText year={year} />
              </div>
            </div>
          </div>
        ) : (
          <div className="min-h-screen bg-white overflow-x-hidden">
            <TopBar />
            <Header onLanguageChange={handleLanguageChange} />
            <main>
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <HeroSection language={selectedLanguage} />
                      <VideoSection language={selectedLanguage} />
                      <FeatureBoxes language={selectedLanguage} />
                      <CategoryGrid language={selectedLanguage} />
                      <InfoSection language={selectedLanguage} />
                      <GermanBrandsSlider language={selectedLanguage} />
                      <ImageSlider language={selectedLanguage} />
                      <AdviserSection language={selectedLanguage} />
                      <NewsletterSection language={selectedLanguage} />
                      <MadeInGermanyExcellenceSection language={selectedLanguage} />
                    </>
                  }
                />
                <Route path="/craftedexcellence" element={<CraftedExcellence language={selectedLanguage} />} />
                <Route path="/worldwidecraft" element={<WorldwideCraft language={selectedLanguage} />} />
                <Route path="/contactform" element={<ContactForm language={selectedLanguage} />} />
                <Route path="/recruitingform" element={<RecruitingForm language={selectedLanguage} />} />
                <Route path="/careers" element={<Careers language={selectedLanguage} />} />
                <Route path="/comingsoon" element={<ComingSoon language={selectedLanguage} />} />
                <Route path="/madeingermanyhistory" element={<MadeInGermanyHistory language={selectedLanguage} />} />
                <Route path="/presscontact" element={<PressContact language={selectedLanguage} />} />
                <Route path="/legalandprotection" element={<LegalAndProtection language={selectedLanguage} />} />
                <Route path="/strategicinvestments" element={<StrategicInvestments language={selectedLanguage} />} />
              </Routes>
            </main>
            <Footer language={selectedLanguage} />
            <GoUpButton language={selectedLanguage} />
          </div>
        )}
      </>
    </Router>
  );
}

export default App;